import React, { createContext, useContext, useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { api, HubRedirectError, TenantSuspendedError } from '../utils/api';
import * as hubAuth from '../utils/hubAuth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // 'in' while a hub login redirect is in flight, 'out' while a hub
  // logout redirect is in flight, null otherwise. Drives a full-screen
  // overlay so the SPA doesn't briefly flash the local Login page (or
  // a stale dashboard) between "we know we're navigating away" and the
  // browser actually unloading.
  const [redirecting, setRedirecting] = useState(null);
  // Set when the backend returns 403 + X-Tenant-Suspended on /auth/me.
  // While set, the SPA renders a dedicated suspended screen instead of
  // bouncing to the hub login (re-logging in won't lift the block).
  const [suspended, setSuspended] = useState(null);
  // 'in-app' | 'redirect' | 'local' — how this deployment signs people
  // in. Read from /api/auth/config so it's deployment config, not a
  // build-time constant baked into a bundle an installed PWA may have
  // cached weeks ago.
  const [loginMode, setLoginMode] = useState(null);

  // Bootstrap auth state on mount.
  //
  // Priority order:
  //   1. A previously-stored local-mode Bearer token (localStorage).
  //   2. Whatever credential api.js can produce — a hub access token
  //      from a previous in-app sign-in (renewed silently if stale), or
  //      the `.estreamly.co` SSO cookie for a desktop browser session.
  //      Either way that's just /api/auth/me; the backend accepts both.
  //
  // A 401 here is not an error state: in-app mode raises
  // LoginRequiredError and we render the login form on this origin.
  useEffect(() => {
    let cancelled = false;

    hubAuth.getAuthConfig().then((config) => {
      if (!cancelled && config) setLoginMode(config.login_mode || null);
    });

    const storedAuth = localStorage.getItem('bb_auth_user');
    if (storedAuth) {
      try {
        const parsedAuth = JSON.parse(storedAuth);
        if (parsedAuth.token && parsedAuth.user) {
          setUser(parsedAuth.user);
          setToken(parsedAuth.token);
          setLoading(false);
          return () => { cancelled = true; };
        }
      } catch (e) {
        localStorage.removeItem('bb_auth_user');
      }
    }

    let didRedirect = false;
    api.getMe()
      .then((me) => {
        if (cancelled) return;
        setUser(me);
        setToken(null);     // the credential lives in hubAuth or a cookie
      })
      .catch((err) => {
        // HubRedirectError: api.js has already kicked off the navigation
        // to hub.estreamly.co. Stay in a loading/redirecting state until
        // the browser unloads — flipping `loading` to false here is what
        // causes the local Login page to flash for a beat in hub mode.
        if (err instanceof HubRedirectError) {
          didRedirect = true;
          if (!cancelled) setRedirecting('in');
          return;
        }
        if (err instanceof TenantSuspendedError) {
          if (!cancelled) {
            setSuspended({ orgId: err.orgId, message: err.message });
          }
          return;
        }
        // LoginRequiredError and plain 401s both mean "show the form".
      })
      .finally(() => {
        if (!cancelled && !didRedirect) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // The hub can end a session out from under us — a refresh token that
  // was replayed gets its whole family revoked. Without this the UI
  // would sit there looking signed in while every request 401s.
  useEffect(() => hubAuth.onSessionLost(() => {
    setUser(null);
    setToken(null);
    setError('Your session ended. Please sign in again.');
  }), []);

  // Load the account once tokens are in hand. Kept separate from the
  // sign-in calls because all three entry points (password, code, and a
  // restored session) need the same follow-up.
  const loadAccount = async () => {
    try {
      const me = await api.getMe();
      setUser(me);
      setToken(null);
      return true;
    } catch (err) {
      if (err instanceof TenantSuspendedError) {
        setSuspended({ orgId: err.orgId, message: err.message });
        return false;
      }
      throw err;
    }
  };

  // ── Sign-in ────────────────────────────────────────────────────────
  //
  // All three of these post to the hub's JSON API from this origin and
  // never navigate. That's what keeps an installed PWA inside its own
  // window — and on iOS, inside the storage container that actually
  // holds the session.

  const requestCode = async (email) => {
    setError(null);
    try {
      await hubAuth.requestCode(email.trim().toLowerCase());
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const verifyCode = async (email, code) => {
    setError(null);
    try {
      await hubAuth.verifyCode(email.trim().toLowerCase(), code.trim());
      return await loadAccount();
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const requestPasswordReset = async (email) => {
    setError(null);
    try {
      await hubAuth.requestPasswordReset(email.trim().toLowerCase());
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const login = async (email, password) => {
    setError(null);

    // Local-mode deployments (USE_HUB_AUTH=false) still authenticate
    // against this backend's own bcrypt users.
    if (loginMode === 'local') {
      try {
        const response = await api.login(email.toLowerCase().trim(), password);
        if (response.access_token && response.user) {
          setUser(response.user);
          setToken(response.access_token);
          localStorage.setItem('bb_auth_user', JSON.stringify({
            token: response.access_token,
            user: response.user,
          }));
          return true;
        }
        setError('Invalid username or password');
        return false;
      } catch (err) {
        setError(err.message || 'Invalid username or password');
        return false;
      }
    }

    try {
      await hubAuth.loginWithPassword(email.trim().toLowerCase(), password);
      return await loadAccount();
    } catch (err) {
      setError(err.message);
      return false;
    }
  };

  const logout = async () => {
    // Show the "Signing out…" overlay before doing anything else so the
    // user never sees the local Login page flash through while we wait
    // for the hub logout URL.
    setRedirecting('out');

    // Drop local auth state immediately — `redirecting` is checked
    // before `isAuthenticated` in AppRoutes/ProtectedRoute, so the
    // overlay stays on screen even with `user` cleared, and a tab
    // close / refresh from this point on lands the user on /login
    // even if the backend hangs. (Codex P1: don't keep the cached
    // session around while we wait for the network.)
    setUser(null);
    setToken(null);
    setError(null);
    localStorage.removeItem('bb_auth_user');

    // Race the logout calls against a short timeout so a slow or hung
    // backend doesn't trap the user on the spinner forever. 5 s is
    // generous for the round-trip; past that we just drop the overlay
    // and let the routes render /login.
    //
    // hubAuth.logout() revokes the refresh token family on the hub, so
    // signing out actually ends the session rather than only forgetting
    // it locally. It clears local storage first, so a timeout here still
    // leaves the client signed out.
    let body = null;
    try {
      body = await Promise.race([
        Promise.all([hubAuth.logout(), api.logout()]).then(([, r]) => r),
        new Promise((resolve) => setTimeout(() => resolve(null), 5000)),
      ]);
    } catch (e) {
      // Best-effort — local state is already gone.
    }

    // Only redirect mode returns a logout_url. In in-app mode there's no
    // parent-domain cookie to clear, and navigating away would drop an
    // installed PWA out of its own window on the way out.
    if (body && body.logout_url) {
      window.location.href = body.logout_url;
      return;
    }
    setRedirecting(null);
  };

  const hasPermission = (permission) => {
    if (!user || !user.permissions) return false;
    return user.permissions.includes(permission);
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isSuperAdmin = () => {
    return !!user?.is_super_admin;
  };

  const switchOrg = async (orgId) => {
    const result = await api.switchOrg(orgId);
    setUser((prev) => prev ? {
      ...prev,
      active_org_id: result.active_org_id,
      org_override: result.active_org_id !== prev.primary_org_id ? result.active_org_id : null,
    } : prev);
    return result;
  };

  const clearOrgOverride = async () => {
    const result = await api.clearOrgOverride();
    setUser((prev) => prev ? {
      ...prev,
      active_org_id: result.active_org_id,
      org_override: null,
    } : prev);
    return result;
  };

  const value = {
    user,
    token,
    login,
    requestCode,
    verifyCode,
    requestPasswordReset,
    logout,
    loading,
    redirecting,
    suspended,
    error,
    setError,
    loginMode,
    hasPermission,
    isAdmin,
    isSuperAdmin,
    switchOrg,
    clearOrgOverride,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Full-screen loading / redirecting overlay. Used by both AppRoutes and
// ProtectedRoute so the "Signing in…" / "Signing out…" / "Loading…"
// states are rendered the same way and never collapse to the local
// Login form between an api.js redirect kickoff and the browser
// actually unloading.
export function AuthOverlay({ phase }) {
  const message =
    phase === 'in' ? 'Signing in…' :
    phase === 'out' ? 'Signing out…' :
    'Loading…';
  return (
    <div
      className="organic o-auth o-auth-status"
      style={{ position: 'fixed', inset: 0, zIndex: 1000 }}
    >
      <div className="o-auth-spinner" />
      <p className="o-auth-sub">{message}</p>
    </div>
  );
}

// Full-screen "your tenant has been suspended" notice. Rendered by
// AppRoutes when /api/auth/me comes back 403 + X-Tenant-Suspended.
// Bouncing back through the hub login won't lift the block, so this is
// a terminal state — the user can sign out (which clears the cookies
// and lets a different account log in) or contact support.
export function SuspendedScreen({ orgId, message, onLogout }) {
  return (
    <div
      className="organic o-auth o-auth-status"
      style={{ position: 'fixed', inset: 0, zIndex: 1000 }}
    >
      <h1 className="o-auth-title">Account suspended</h1>
      {/* The message carries the alert colour rather than the heading —
          the palette's warmth shouldn't make a blocked account read as
          a normal, calm screen. */}
      <p className="o-auth-msg o-auth-msg-error">
        {message || 'Your organization account has been suspended. Please contact your administrator.'}
      </p>
      {orgId && (
        <p className="o-auth-code-hint">
          Organization id: <code>{orgId}</code>
        </p>
      )}
      <button type="button" className="btn btn-secondary" onClick={onLogout}>
        Sign out
      </button>
    </div>
  );
}

// Protected route wrapper component
export function ProtectedRoute({ children, requiredPermission = null }) {
  const { isAuthenticated, loading, redirecting, hasPermission } = useAuth();

  if (redirecting) {
    return <AuthOverlay phase={redirecting} />;
  }
  if (loading) {
    return <AuthOverlay phase="loading" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div style={{ 
        padding: '2rem', 
        textAlign: 'center',
        maxWidth: '600px',
        margin: '4rem auto'
      }}>
        <h2 style={{ color: 'var(--danger)', marginBottom: '1rem' }}>
          Access Denied
        </h2>
        <p style={{ color: 'var(--text-secondary)' }}>
          You don't have permission to view this page.
        </p>
      </div>
    );
  }
  
  return children;
}
